import * as Sentry from "@sentry/react";
import React from "react";
import {
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";

/**
 * Sentry DSN (Data Source Name) for error tracking.
 *
 * SECURITY NOTE: DSNs are safe to keep public and can be hardcoded in client-side code.
 * They only allow submission of new events and do not provide read access to any data.
 *
 * From Sentry documentation:
 * "DSNs are safe to keep public because they only allow submission of new events
 * and related event data; they do not allow read access to any information."
 *
 * If abuse occurs, DSNs can be rotated in Sentry project settings under:
 * [Project] > Settings > SDK Setup > Client Keys (DSN)
 */
const SENTRY_DSN = "https://562b03ee1a0e17fa07a6cb228435f586@o4509684240941056.ingest.us.sentry.io/4509905938743296";

type SentryConfig = {
    dsn: string;
    environment?: string;
    enabled: boolean;
};

/**
 * Get Sentry configuration
 */
function getSentryConfig(): SentryConfig {
    // In development, Sentry is disabled by default
    // Can be enabled by setting VITE_SENTRY_ENABLED=true
    const isDevelopment = process.env.NODE_ENV === "development";
    const isTest = process.env.NODE_ENV === "test";
    const explicitlyEnabled = import.meta.env.VITE_SENTRY_ENABLED === "true";

    return {
        dsn: SENTRY_DSN,
        environment: process.env.NODE_ENV || "development",
        enabled: !isTest && (!isDevelopment || explicitlyEnabled),
    };
}

/**
 * Initialize Sentry client.
 * Called at module scope so it runs before the router is created in app.tsx.
 * The React Router v6 tracing integration requires Sentry.init() before createBrowserRouter().
 */
function initSentry(): void {
    const sentryConfig = getSentryConfig();

    if (!sentryConfig.enabled) {
        return;
    }

    Sentry.init({
        dsn: sentryConfig.dsn,
        tunnel: "/sentry-tunnel",
        environment: sentryConfig.environment || "development",
        integrations: [
            // React hooks and router utilities are passed explicitly because the Sentry
            // package doesn't bundle React or React Router — it needs these to subscribe
            // to route changes and create tracing spans from within our React tree.
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        tracesSampleRate: 0.01,
    });
}

// Run init at module scope — must execute before app.tsx creates the router.
initSentry();

/**
 * Wrapped createBrowserRouter that instruments routes with Sentry tracing.
 * Use this instead of importing createBrowserRouter directly from react-router-dom.
 */
export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
